import React, { memo, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Group,
  Menu,
  ScrollArea,
  Tabs,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconArrowBarRight,
  IconArrowsDiff,
  IconExternalLink,
  IconRefresh,
  IconX,
} from "@tabler/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TabRouter from "./tabRouter";
import { AppRouteTree } from "../routes/appRoutes";
import { useTabActions, useTabState } from "../context/tabContext";
import { buildTabLocation, TAB_BAR_STICKY_HEIGHT } from "../utils/tabNavigation";

const useStyles = createStyles((theme) => ({
  tabShell: {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 60px)",
  },
  tabListWrapper: {
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
    borderBottom: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[4]
    }`,
    position: "sticky",
    top: 0,
    zIndex: 150,
    flexShrink: 0,
    padding: "1px 4px 0",
  },
  scrollViewport: {
    paddingBottom: 2,
  },
  tabsList: {
    flexWrap: "nowrap",
    gap: 0,
    borderBottom: "none",
    backgroundColor: "transparent",
  },
  tab: {
    border: "none",
    borderRadius: theme.radius.sm,
    backgroundColor: "transparent",
    padding: "1px 10px",
    height: "auto",
    minHeight: "unset",
    lineHeight: 1.2,
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[1]
        : theme.colors.gray[7],
    "&:not(:first-of-type)": {
      borderLeft: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[4]
          : theme.colors.gray[4]
      }`,
    },
    "&[data-active]": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.white,
      color:
        theme.colorScheme === "dark" ? theme.white : theme.black,
      boxShadow:
        theme.colorScheme === "dark"
          ? undefined
          : "0 1px 3px rgba(0, 0, 0, 0.08)",
      borderLeftColor: "transparent !important",
      "& + &": {
        borderLeftColor: "transparent !important",
      },
      "& .mantine-Text-root, & .mantine-ActionIcon-root": {
        color: "inherit",
        fontWeight: 500,
      },
      "&:hover": {
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[5]
            : theme.white,
        color:
          theme.colorScheme === "dark" ? theme.white : theme.black,
      },
    },
    "&:hover:not([data-active])": {
      backgroundColor: "transparent",
      color:
        theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
    },
  },
  tabLabel: {
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  panel: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    zIndex: 1,
    paddingTop: theme.spacing.md,
  },
}));

function TabLocationSync({ tabId }) {
  const location = useLocation();
  const { updateTabLocation } = useTabActions();
  const { activeTabId } = useTabState();

  useEffect(() => {
    if (tabId !== activeTabId) return;
    updateTabLocation(tabId, location.pathname, location.search);
  }, [
    tabId,
    activeTabId,
    location.pathname,
    location.search,
    updateTabLocation,
  ]);

  return null;
}

const TabPanel = memo(function TabPanel({ tab }) {
  const initialEntry = buildTabLocation(tab.pathname, tab.search);

  return (
    <TabRouter initialEntry={initialEntry}>
      <TabLocationSync tabId={tab.id} />
      <AppRouteTree />
    </TabRouter>
  );
});

function TabContextMenu({
  tab,
  tabs,
  opened,
  onOpen,
  onClose,
  onRefresh,
  onCloseTab,
  onCloseRight,
  onCloseOther,
  onOpenInNewWindow,
  classes,
  t,
  closeTab,
}) {
  const tabIndex = tabs.findIndex((item) => item.id === tab.id);
  const hasClosableRight = tabs
    .slice(tabIndex + 1)
    .some((item) => item.closable);
  const hasOtherClosable = tabs.some(
    (item) => item.id !== tab.id && item.closable
  );

  return (
    <Menu
      opened={opened}
      onChange={(nextOpened) => {
        if (!nextOpened) onClose();
      }}
      position="bottom-start"
      withinPortal
    >
      <Menu.Target>
        <Tabs.Tab
          value={tab.id}
          onContextMenu={(event) => {
            event.preventDefault();
            onOpen();
          }}
          onMouseDown={(event) => {
            if (event.button === 1 && tab.closable) {
              event.preventDefault();
              closeTab(tab.id);
            }
          }}
        >
          <Group spacing={4} noWrap>
            <Text size="sm" className={classes.tabLabel}>
              {t(tab.label)}
            </Text>
            {tab.closable && (
              <ActionIcon
                size={16}
                variant="transparent"
                onClick={(event) => {
                  event.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                <IconX size={10} />
              </ActionIcon>
            )}
          </Group>
        </Tabs.Tab>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<IconRefresh size={14} />} onClick={onRefresh}>
          {t("Refresh")}
        </Menu.Item>
        <Menu.Item
          icon={<IconX size={14} />}
          onClick={onCloseTab}
          disabled={!tab.closable}
        >
          {t("Close")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={<IconArrowBarRight size={14} />}
          onClick={onCloseRight}
          disabled={!hasClosableRight}
        >
          {t("Close Right Tabs")}
        </Menu.Item>
        <Menu.Item
          icon={<IconArrowsDiff size={14} />}
          onClick={onCloseOther}
          disabled={!hasOtherClosable}
        >
          {t("Close Other")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={<IconExternalLink size={14} />}
          onClick={onOpenInNewWindow}
        >
          {t("Open in new Window")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const TabBar = memo(function TabBar({
  tabs,
  activeTabId,
  activateTab,
  closeTab,
  closeTabsToRight,
  closeOtherTabs,
  refreshTab,
  classes,
}) {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const prevTabCountRef = useRef(tabs.length);
  const [contextTabId, setContextTabId] = useState(null);

  useEffect(() => {
    const tabCountIncreased = tabs.length > prevTabCountRef.current;
    prevTabCountRef.current = tabs.length;

    if (!tabCountIncreased) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        viewport.scrollTo({
          left: viewport.scrollWidth,
          behavior: "smooth",
        });
      });
    });
  }, [tabs.length]);

  useEffect(() => {
    if (contextTabId && !tabs.some((tab) => tab.id === contextTabId)) {
      setContextTabId(null);
    }
  }, [tabs, contextTabId]);

  const closeContextMenu = () => setContextTabId(null);

  return (
    <Box className={classes.tabListWrapper}>
      <ScrollArea
        type="never"
        offsetScrollbars
        viewportRef={viewportRef}
        classNames={{ viewport: classes.scrollViewport }}
      >
        <Tabs
          value={activeTabId}
          onTabChange={activateTab}
          variant="pills"
          radius="sm"
          classNames={{
            tabsList: classes.tabsList,
            tab: classes.tab,
          }}
          styles={(theme) => ({
            tab: {
              paddingTop: 1,
              paddingBottom: 1,
              height: "auto",
              minHeight: "unset",
              "&[data-active]:hover": {
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[5]
                    : theme.white,
              },
            },
          })}
        >
          <Tabs.List>
            {tabs.map((tab) => (
              <TabContextMenu
                key={tab.id}
                tab={tab}
                tabs={tabs}
                opened={contextTabId === tab.id}
                onOpen={() => setContextTabId(tab.id)}
                onClose={closeContextMenu}
                onRefresh={() => {
                  refreshTab(tab.id);
                  closeContextMenu();
                }}
                onCloseTab={() => {
                  closeTab(tab.id);
                  closeContextMenu();
                }}
                onCloseRight={() => {
                  closeTabsToRight(tab.id);
                  closeContextMenu();
                }}
                onCloseOther={() => {
                  closeOtherTabs(tab.id);
                  closeContextMenu();
                }}
                onOpenInNewWindow={() => {
                  const url = `${window.location.origin}${buildTabLocation(
                    tab.pathname,
                    tab.search
                  )}`;
                  window.open(url, "_blank");
                  closeContextMenu();
                }}
                classes={classes}
                t={t}
                closeTab={closeTab}
              />
            ))}
          </Tabs.List>
        </Tabs>
      </ScrollArea>
    </Box>
  );
});

export default function TabShell() {
  const { classes } = useStyles();
  const { tabs, activeTabId, tabRefreshKeys } = useTabState();
  const {
    activateTab,
    closeTab,
    closeTabsToRight,
    closeOtherTabs,
    refreshTab,
  } = useTabActions();

  return (
    <Box
      className={classes.tabShell}
      style={{ "--mbid-tab-bar-height": `${TAB_BAR_STICKY_HEIGHT}px` }}
    >
      <TabBar
        tabs={tabs}
        activeTabId={activeTabId}
        activateTab={activateTab}
        closeTab={closeTab}
        closeTabsToRight={closeTabsToRight}
        closeOtherTabs={closeOtherTabs}
        refreshTab={refreshTab}
        classes={classes}
      />

      <Box className={classes.panel}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={`${tab.id}-${tabRefreshKeys[tab.id] ?? 0}`}
              style={{ display: isActive ? "block" : "none" }}
              aria-hidden={!isActive}
            >
              <TabPanel tab={tab} />
            </div>
          );
        })}
      </Box>
    </Box>
  );
}
